import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEmergencyEvents } from '../context/EmergencyEventContext';
import '../css/EmergencyAlertModal.css';

if (typeof document !== 'undefined') {
    Modal.setAppElement('#root');
}

const formatOccurredAt = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('ko-KR');
};

// 응급 알림 소리. 기관 담당자가 자리를 비웠다 돌아와도 알아채야 해서 짧은 한 번이 아니라
// 사이렌처럼 반복한다(2026-09-17 요청).
//
// ⚠️ 소리는 못 들을 수 있다고 보고 만든다 — 브라우저는 사용자가 한 번이라도 클릭하기 전에는
//    소리를 막고, 스피커가 꺼져 있을 수도 있다. 팝업 자체가 알림의 본체이고 소리는 거들 뿐이다.
const ALARM_BURSTS = 12;          // 반복 횟수
const ALARM_BURST_SECONDS = 0.45; // 한 번 울리는 길이
const ALARM_GAP_SECONDS = 0.2;    // 사이 쉬는 길이 (총 약 7.8초)
const ALARM_PEAK_GAIN = 0.32;     // 예전 0.16 의 두 배
const ALARM_HIGH_HZ = 988;
const ALARM_LOW_HZ = 740;

const useEmergencyAlertSound = (event) => {
    const audioContextRef = useRef(null);
    const interactionEnabledRef = useRef(false);
    const pendingIdRef = useRef(null);
    const playedIdsRef = useRef(new Set());
    const activeNodesRef = useRef([]);

    // 확인을 누르거나 다른 알림으로 넘어가면 울리던 소리를 끊는다.
    // 끄지 않으면 확인한 뒤에도 8초 가까이 계속 울린다.
    const stopAlarm = useCallback(() => {
        activeNodesRef.current.forEach((node) => {
            try {
                node.stop();
            } catch (error) {
                // 이미 끝난 소리다.
            }
        });
        activeNodesRef.current = [];
    }, []);

    const playTone = useCallback(async (eventId) => {
        if (eventId === undefined || eventId === null) return;
        const key = String(eventId);
        if (playedIdsRef.current.has(key)) return;

        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return;

            if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
                audioContextRef.current = new AudioContextClass();
            }

            const context = audioContextRef.current;
            if (context.state === 'suspended') await context.resume();
            if (context.state !== 'running') return;

            stopAlarm();
            const start = context.currentTime;

            // 한 번에 다 예약한다. setInterval 로 돌리면 탭이 뒤로 가 있을 때 타이머가
            // 느려져 박자가 흐트러진다 — 오디오 시계는 그 영향을 받지 않는다.
            for (let i = 0; i < ALARM_BURSTS; i += 1) {
                const at = start + i * (ALARM_BURST_SECONDS + ALARM_GAP_SECONDS);
                const oscillator = context.createOscillator();
                const gain = context.createGain();

                // 두 음을 오가는 사이렌. 한 음만 길게 끌면 배경음처럼 들려 놓치기 쉽다.
                oscillator.type = 'square';
                oscillator.frequency.setValueAtTime(ALARM_HIGH_HZ, at);
                oscillator.frequency.setValueAtTime(ALARM_LOW_HZ, at + ALARM_BURST_SECONDS / 2);

                gain.gain.setValueAtTime(0.0001, at);
                gain.gain.exponentialRampToValueAtTime(ALARM_PEAK_GAIN, at + 0.02);
                gain.gain.setValueAtTime(ALARM_PEAK_GAIN, at + ALARM_BURST_SECONDS - 0.05);
                gain.gain.exponentialRampToValueAtTime(0.0001, at + ALARM_BURST_SECONDS);

                oscillator.connect(gain);
                gain.connect(context.destination);
                oscillator.start(at);
                oscillator.stop(at + ALARM_BURST_SECONDS);
                activeNodesRef.current.push(oscillator);
            }
            playedIdsRef.current.add(key);
        } catch (error) {
            // 브라우저 정책이나 하드웨어가 막아도 팝업은 그대로 보인다.
        }
    }, [stopAlarm]);

    useEffect(() => {
        const enableAudio = () => {
            interactionEnabledRef.current = true;
            window.removeEventListener('pointerdown', enableAudio, true);
            window.removeEventListener('keydown', enableAudio, true);
            if (pendingIdRef.current !== null) {
                playTone(pendingIdRef.current);
            }
        };

        window.addEventListener('pointerdown', enableAudio, true);
        window.addEventListener('keydown', enableAudio, true);
        return () => {
            window.removeEventListener('pointerdown', enableAudio, true);
            window.removeEventListener('keydown', enableAudio, true);
        };
    }, [playTone]);

    useEffect(() => {
        pendingIdRef.current = event?.id ?? null;
        if (event && interactionEnabledRef.current) {
            playTone(event.id);
        }
        if (!event) stopAlarm();
    }, [event, playTone, stopAlarm]);

    // 화면을 떠나면(로그아웃 등) 소리도 멈춘다.
    useEffect(() => stopAlarm, [stopAlarm]);
};

const EmergencyAlertModal = () => {
    const { currentEvent, pendingCount, acknowledgeEvent } = useEmergencyEvents();
    const navigate = useNavigate();
    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const [error, setError] = useState('');

    useEmergencyAlertSound(currentEvent);

    useEffect(() => {
        setIsAcknowledging(false);
        setError('');
    }, [currentEvent?.id]);

    const handleAcknowledge = async () => {
        if (!currentEvent || isAcknowledging) return;
        setIsAcknowledging(true);
        setError('');

        try {
            await acknowledgeEvent(currentEvent.id);
        } catch (acknowledgeError) {
            setError(acknowledgeError.message || '응급 이벤트 확인에 실패했습니다.');
            setIsAcknowledging(false);
        }
    };

    const handleOpenDetails = async () => {
        if (!currentEvent || isAcknowledging
            || currentEvent.appUserId === undefined || currentEvent.appUserId === null) return;

        setIsAcknowledging(true);
        setError('');
        try {
            await acknowledgeEvent(currentEvent.id);
            navigate(`/DashboardDetail/${encodeURIComponent(currentEvent.appUserId)}`);
        } catch (acknowledgeError) {
            setError(acknowledgeError.message || '응급 이벤트 확인에 실패했습니다.');
            setIsAcknowledging(false);
        }
    };

    const occurredAt = formatOccurredAt(currentEvent?.occurredAt);

    return (
        <Modal
            isOpen={Boolean(currentEvent)}
            onRequestClose={() => {}}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
            shouldReturnFocusAfterClose
            contentLabel="응급 상황 알림"
            role="alertdialog"
            overlayClassName="sds-modal-overlay emergency-alert-overlay"
            className="sds-modal emergency-alert-modal"
        >
            {currentEvent && (
                <>
                    <div className="sds-modal__head emergency-alert-modal__head">
                        <span className="sds-list__icon sds-list__icon--danger emergency-alert-modal__icon" aria-hidden="true">
                            <AlertTriangle size={20} />
                        </span>
                        <div>
                            <h2 className="sds-modal__title">응급 상황 알림</h2>
                            {pendingCount > 1 && (
                                <p className="emergency-alert-modal__count">대기 알림 {pendingCount}건</p>
                            )}
                        </div>
                    </div>

                    <div className="sds-modal__body">
                        {/* 본문은 알림 문구다. 빨간 배경은 여기 한 곳에만 쓴다 —
                            팝업 전체를 빨갛게 칠하면 무엇이 문제인지가 오히려 흐려진다. */}
                        <p className="sds-notice sds-notice--error emergency-alert-modal__message">
                            {currentEvent.message || '사용자에게 응급 상황이 감지되었습니다.'}
                        </p>
                        <div className="sds-list emergency-alert-modal__facts">
                            {currentEvent.userName && (
                                <div className="sds-list__row">
                                    <div className="sds-list__body"><b>사용자</b></div>
                                    <div className="sds-list__spacer" />
                                    <div className="sds-list__value">{currentEvent.userName}</div>
                                </div>
                            )}
                            {occurredAt && (
                                <div className="sds-list__row">
                                    <div className="sds-list__body"><b>발생 시각</b></div>
                                    <div className="sds-list__spacer" />
                                    <div className="sds-list__value">{occurredAt}</div>
                                </div>
                            )}
                        </div>
                        {error && <p className="sds-notice sds-notice--error" role="alert">{error}</p>}
                    </div>

                    <div className="sds-modal__foot">
                        <button
                            type="button"
                            className="sds-btn sds-btn--secondary"
                            onClick={handleOpenDetails}
                            disabled={isAcknowledging
                                || currentEvent.appUserId === undefined
                                || currentEvent.appUserId === null}
                        >
                            사용자 상세
                        </button>
                        <button
                            type="button"
                            className="sds-btn sds-btn--primary"
                            onClick={handleAcknowledge}
                            disabled={isAcknowledging}
                            autoFocus
                        >
                            {isAcknowledging ? '확인 중...' : '확인'}
                        </button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default EmergencyAlertModal;
